import { useForm } from "react-hook-form";
import { ChevronRight, X } from "lucide-react";
import { toast } from "../../../lib/toast";
import { useSetup } from "../SetupContext";
import { businessUnits, spendCategories, regions, timelines } from "../../../data/mock";

export function SubStep1({ onNext, onCancel }: { onNext: () => void; onCancel: () => void }) {
  const { form, setField } = useSetup();
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      projectName: form.projectName, businessUnit: form.businessUnit,
      category: form.category, region: form.region,
      budget: form.budget, timeline: form.timeline,
    },
  });

  const onSubmit = (data: any) => {
    Object.entries(data).forEach(([k, v]) => setField(k as any, v as any));
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="setup-form-header">
        <div className="setup-form-title">Step 1 · Project details</div>
        <div className="setup-form-desc">Identify the engagement and scope.</div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-group__label" htmlFor="projectName">Project Name *</label>
          <input
            id="projectName"
            className="form-group__input"
            placeholder="e.g. Q4 Indirect Spend Review"
            aria-invalid={!!errors.projectName}
            {...register("projectName", { required: "Project name is required" })}
            onChange={(e) => setField("projectName", e.target.value)}
          />
          {errors.projectName && <span style={{ fontSize: 11, color: "#ef4444" }}>{errors.projectName.message}</span>}
        </div>

        <div className="form-group">
          <label className="form-group__label" htmlFor="businessUnit">Business Unit *</label>
          <div className="form-group__select-wrap">
            <select
              id="businessUnit"
              className="form-group__select"
              aria-invalid={!!errors.businessUnit}
              {...register("businessUnit", { required: true })}
              onChange={(e) => setField("businessUnit", e.target.value)}
            >
              <option value="">Select business unit…</option>
              {businessUnits.map((u) => <option key={u}>{u}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-group__label" htmlFor="category">Spend Category *</label>
          <div className="form-group__select-wrap">
            <select
              id="category"
              className="form-group__select"
              aria-invalid={!!errors.category}
              {...register("category", { required: true })}
              onChange={(e) => setField("category", e.target.value)}
            >
              <option value="">Select category…</option>
              {spendCategories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-group__label" htmlFor="region">Region</label>
          <div className="form-group__select-wrap">
            <select
              id="region"
              className="form-group__select"
              {...register("region")}
              onChange={(e) => setField("region", e.target.value)}
            >
              <option value="">Select region…</option>
              {regions.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-group__label" htmlFor="budget">Annual Budget (USD)</label>
          <input
            id="budget"
            className="form-group__input"
            placeholder="e.g. 5,000,000"
            type="text"
            inputMode="numeric"
            {...register("budget")}
            onChange={(e) => setField("budget", e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-group__label" htmlFor="timeline">Project Timeline</label>
          <div className="form-group__select-wrap">
            <select
              id="timeline"
              className="form-group__select"
              {...register("timeline")}
              onChange={(e) => setField("timeline", e.target.value)}
            >
              {timelines.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="wizard-action-bar">
        <button type="button" className="action-bar__cancel btn btn--outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn--outline m-left-auto" onClick={() => toast.success("Draft saved")}>
          Save Draft
        </button>
        <button type="submit" className="btn btn--primary">
          Continue
        </button>
      </div>
    </form>
  );
}
